import { Avatar, Flex, Text, Box } from '@chakra-ui/react';
import { useGetMembersQuery, GetMembersQueryVariables } from 'gql/graphql';
import {
  Select as ReactSelect,
  chakraComponents,
  OptionProps,
  GroupBase,
  OnChangeValue,
  InputActionMeta,
} from 'chakra-react-select';
import React, { useMemo, useState, useRef } from 'react';
import { MemberOption } from 'features/user-groups/components/AddMembersDropdown/types';
import {
  filterOptions,
  getLoadingMessage,
  getNoOptionsMessage,
} from 'features/user-groups/components/AddMembersDropdown/utils';

export const customInviteComponents = {
  Option: (
    props: OptionProps<MemberOption, boolean, GroupBase<MemberOption>>,
  ) => {
    const { label, email, icon } = props.data;
    return (
      <chakraComponents.Option {...props}>
        <Flex gap={2} align='center'>
          <Avatar
            size='sm'
            name={label}
            src={icon ?? ''}
            bg='teal.700'
            textColor='white'
          />
          <Flex direction='column' gap={0.5}>
            <Text>{label}</Text>
            <Text variant='subtle'>{email}</Text>
          </Flex>
        </Flex>
      </chakraComponents.Option>
    );
  },
};

export interface InviteMembersDropdownProps {
  selected: MemberOption[];
  onMembersSelect: (selected: MemberOption[]) => void;
}

const InviteMembersDropdown: React.FC<InviteMembersDropdownProps> = ({
  selected,
  onMembersSelect,
}) => {
  const selectRef = useRef<any>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const { data: membersData, loading: membersLoading } = useGetMembersQuery({
    variables: {
      skip: 0,
      take: 100,
    } as GetMembersQueryVariables,
    fetchPolicy: 'cache-and-network',
  });

  const members: MemberOption[] = useMemo(
    () =>
      (membersData?.organization?.members?.items || []).map((member) => ({
        value: member.id,
        label: `${member.givenName} ${member.familyName}`,
        email: member.email,
        icon: member.picture,
      })),
    [membersData?.organization?.members?.items],
  );

  const handleChange = (newValue: OnChangeValue<MemberOption, true>) => {
    const selectedOptions = (newValue as MemberOption[]) ?? [];
    onMembersSelect(selectedOptions);
    setInputValue('');
    selectRef.current?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const parts = paste.split(/[,\s]+/).filter(Boolean);
    const matched = members.filter((m) => parts.includes(m.email));
    if (matched.length) {
      const merged = Array.from(
        new Map([...selected, ...matched].map((m) => [m.email, m])).values(),
      );
      onMembersSelect(merged as MemberOption[]);
    }
  };

  const handleInputChange = (value: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(value);
      selectRef.current?.focus();
    }
  };

  return (
    <Box onPaste={handlePaste}>
      <ReactSelect
        ref={selectRef}
        closeMenuOnSelect={false}
        /* @ts-ignore: prop from react-select */
        clearInputOnSelect={true}
        /* @ts-ignore: prop from react-select */
        blurInputOnSelect={false}
        aria-label='Invite Members Select'
        options={members}
        onChange={handleChange}
        value={selected}
        placeholder='Select or add members'
        isDisabled={membersLoading}
        isLoading={membersLoading}
        noOptionsMessage={getNoOptionsMessage}
        loadingMessage={getLoadingMessage}
        components={{
          ...customInviteComponents,
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
          ClearIndicator: () => null,
        }}
        filterOption={filterOptions}
        isMulti
        inputValue={inputValue}
        onInputChange={handleInputChange}
      />
      <Flex justifyContent='space-between' mt={1}>
        {selected.length > 0 && (
          <Text
            fontSize='xs'
            color='purple.500'
            cursor='pointer'
            onClick={() => onMembersSelect([])}
          >
            Clear list
          </Text>
        )}
        {selected.length < members.length && (
          <Text
            fontSize='xs'
            color='purple.500'
            cursor='pointer'
            onClick={() => onMembersSelect(members)}
          >
            Select all members
          </Text>
        )}
      </Flex>
    </Box>
  );
};

export default InviteMembersDropdown;
